import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import LeadDetail from "@/pages/LeadDetail";
import Surveys from "@/pages/Surveys";
import SalesVisits from "@/pages/SalesVisits";
import Proposals from "@/pages/Proposals";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import WorkScheduler from "@/pages/WorkScheduler";
import Operations from "@/pages/Operations";
import DeliveryChecklists from "@/pages/DeliveryChecklists";
import DocumentationTracking from "@/pages/DocumentationTracking";
import MyAssignments from "@/pages/MyAssignments";
import MyLeaves from "@/pages/MyLeaves";
import LeaveManagement from "@/pages/LeaveManagement";
import Tickets from "@/pages/Tickets";
import WhatsAppLog from "@/pages/WhatsAppLog";
import Users from "@/pages/Users";
import GeneralSettings from "@/pages/GeneralSettings";
import CalendarPage from "@/pages/CalendarPage";
import Finance from "@/pages/Finance";
import AMC from "@/pages/AMC";
import AMCCalendar from "@/pages/AMCCalendar";
import CustomerPortal from "@/pages/CustomerPortal";
import PublicProposalView from "@/pages/PublicProposalView";
import SystemTemplates from "@/pages/SystemTemplates";
import Permissions from "@/pages/Permissions";
import PurchasingDashboard from "@/pages/purchasing/PurchasingDashboard";
import PVendors from "@/pages/purchasing/Vendors";
import PProducts from "@/pages/purchasing/Products";
import PBrands from "@/pages/purchasing/Brands";
import PPriceEnquiries from "@/pages/purchasing/PriceEnquiries";
import PPurchaseOrders from "@/pages/purchasing/PurchaseOrders";
import PPriceHistory from "@/pages/purchasing/PriceHistory";
import PGRN from "@/pages/purchasing/GRN";
import { StockDashboard, StockCurrent, StockLedger, StockLow, StockReports } from "@/pages/Stock";
import PProductComparison from "@/pages/purchasing/ProductComparison";
import PVendorPerformance from "@/pages/purchasing/VendorPerformance";
import PPlaceholder from "@/pages/purchasing/Placeholder";
import "@/App.css";

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <div className="p-8 label-eyebrow">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/portal/:qr" element={<CustomerPortal />} />
      <Route path="/share/proposal/:token" element={<PublicProposalView />} />
      <Route
        path="/"
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="leads" element={<Leads />} />
        <Route path="leads/:id" element={<LeadDetail />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="sales-visits" element={<SalesVisits />} />
        <Route path="surveys" element={<Surveys />} />
        <Route path="proposals" element={<Proposals />} />
        <Route path="system-templates" element={<SystemTemplates />} />
        <Route path="permissions" element={<Permissions />} />
        <Route path="projects" element={<Projects />} />
        <Route path="projects/:id" element={<ProjectDetail />} />
        <Route path="work-scheduler" element={<WorkScheduler />} />
        <Route path="operations" element={<Operations />} />
        <Route path="delivery-checklists" element={<DeliveryChecklists />} />
        <Route path="stock" element={<StockDashboard />} />
        <Route path="stock/current" element={<StockCurrent />} />
        <Route path="stock/ledger" element={<StockLedger />} />
        <Route path="stock/low" element={<StockLow />} />
        <Route path="stock/reports" element={<StockReports />} />
        <Route path="documentation-tracking" element={<DocumentationTracking />} />
        <Route path="my-assignments" element={<MyAssignments />} />
        <Route path="my-leaves" element={<MyLeaves />} />
        <Route path="hr/leave" element={<LeaveManagement />} />
        <Route path="tickets" element={<Tickets />} />
        <Route path="finance" element={<Finance />} />
        <Route path="amc" element={<AMC />} />
        <Route path="amc/calendar" element={<AMCCalendar />} />
        <Route path="users" element={<Users />} />
        <Route path="general-settings" element={<GeneralSettings />} />
        <Route path="whatsapp" element={<WhatsAppLog />} />
        <Route path="purchasing" element={<PurchasingDashboard />} />
        <Route path="purchasing/vendors" element={<PVendors />} />
        <Route path="purchasing/products" element={<PProducts />} />
        <Route path="purchasing/brands" element={<PBrands />} />
        <Route path="purchasing/price-enquiries" element={<PPriceEnquiries />} />
        <Route path="purchasing/purchase-orders" element={<PPurchaseOrders />} />
        <Route path="purchasing/price-history" element={<PPriceHistory />} />
        <Route path="purchasing/comparison" element={<PProductComparison />} />
        <Route path="purchasing/vendor-performance" element={<PVendorPerformance />} />
        <Route path="purchasing/grn" element={<PGRN />} />
        <Route path="purchasing/reports" element={<PPlaceholder title="Purchasing Reports" subtitle="Purchase by vendor / product / brand, price trend, vendor performance, pending deliveries, PO status." phase="Phase 4" />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
